import { useState, useEffect } from 'react';
import { Plus, Calendar, Check, X, UserCheck, UserX, Edit, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

export default function LecturerAttendance() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showMarkModal, setShowMarkModal] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [students, setStudents] = useState([]);
  const [grades, setGrades] = useState([]);
  const [formData, setFormData] = useState({ subject_id: '', grade_id: '', session_date: '', session_description: '' });
  const [attendanceData, setAttendanceData] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [dateEditSession, setDateEditSession] = useState(null);
  const [dateEditValue, setDateEditValue] = useState('');
  const [savingDate, setSavingDate] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [sessionsRes, subjectsRes] = await Promise.all([
        api.get('/lecturer/attendance/sessions'),
        api.get('/lecturer/subjects')
      ]);
      if (sessionsRes.data.success && sessionsRes.data.data) {
        setSessions(sessionsRes.data.data.sessions || []);
      }
      if (subjectsRes.data.success && subjectsRes.data.data) {
        const grades = subjectsRes.data.data.grades || [];
        // Keep grades for grade selection and also flatten subjects
        setGrades(grades);
        const allSubjects = [];
        grades.forEach(grade => {
          grade.subjects.forEach(subject => {
            allSubjects.push({
              ...subject,
              grade_id: grade.grade_id,
              grade_name: grade.grade_name
            });
          });
        });
        setSubjects(allSubjects);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleGradeChange = async (gradeId) => {
    const grade = grades.find(g => g.grade_id === parseInt(gradeId));
    setFormData({ ...formData, subject_id: '', grade_id: gradeId });

    if (gradeId) {
      try {
        const res = await api.get('/lecturer/students');
        const allStudents = res.data.students || res.data.data?.students || [];
        const gradeStudents = allStudents.filter(s => s.grade_id === parseInt(gradeId));
        setStudents(gradeStudents);
        setAttendanceData(gradeStudents.map(s => ({ student_id: s.user_id, status: '' })));
      } catch (err) {
      }
    } else {
      setStudents([]);
      setAttendanceData([]);
    }
  };


  const toggleAttendance = (studentId) => {
    setAttendanceData(prev => prev.map(a => {
      if (a.student_id === studentId) {
        if (a.status === '') return { ...a, status: 'Present' };
        if (a.status === 'Present') return { ...a, status: 'Absent' };
        return { ...a, status: '' };
      }
      return a;
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const payload = {
        subject_id: formData.subject_id || null,
        grade_id: formData.grade_id,
        session_date: formData.session_date,
        session_description: formData.session_description,
        attendance_data: attendanceData
      };

      const res = editingSessionId
        ? await api.put(`/lecturer/attendance/sessions?sessionId=${editingSessionId}`, payload)
        : await api.post('/lecturer/attendance/sessions', payload);
      if (res.data.success) {
        setSuccess(editingSessionId ? 'Attendance session updated successfully!' : 'Attendance recorded successfully!');
        setShowModal(false);
        setEditingSessionId(null);
        setFormData({ subject_id: '', grade_id: '', session_date: '', session_description: '' });
        setStudents([]);
        setAttendanceData([]);
        loadData();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to record attendance');
    }
  };

  const openEditModal = (session) => {
    setEditingSessionId(session.session_id);
    setFormData({
      subject_id: session.subject_id || '',
      grade_id: session.grade_id || '',
      session_date: session.session_date ? session.session_date.split('T')[0] : '',
      session_description: session.session_description || ''
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingSessionId(null);
    setFormData({ subject_id: '', grade_id: '', session_date: '', session_description: '' });
    setStudents([]);
    setAttendanceData([]);
    setError('');
    setShowModal(true);
  };

  const openDateEditModal = (session) => {
    setDateEditSession(session);
    setDateEditValue(session.session_date ? session.session_date.split('T')[0] : '');
    setError('');
  };

  const saveSessionDate = async (event) => {
    event.preventDefault();
    if (!dateEditSession || !dateEditValue) return;

    try {
      setSavingDate(true);
      setError('');
      const res = await api.put(`/lecturer/attendance/sessions?sessionId=${dateEditSession.session_id}`, {
        subject_id: dateEditSession.subject_id || null,
        grade_id: dateEditSession.grade_id,
        session_date: dateEditValue,
        session_description: dateEditSession.session_description || ''
      });
      if (res.data.success) {
        setDateEditSession(null);
        setSuccess('Attendance date updated successfully!');
        await loadData();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update attendance date');
    } finally {
      setSavingDate(false);
    }
  };

  const handleDeleteSession = async (session) => {
    if (!confirm('Delete this attendance session? This will remove all attendance records for this session.')) return;

    try {
      const res = await api.delete(`/lecturer/attendance/sessions?sessionId=${session.session_id}`);
      if (res.data.success) {
        setSuccess('Attendance session deleted successfully!');
        loadData();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete attendance session');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Attendance Management</h1>
          <p className="text-gray-500">Mark and manage session attendance</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowDateFilter(prev => !prev)}
              title="Filter attendance by date"
              aria-label="Filter attendance by date"
              aria-expanded={showDateFilter}
              className={`p-2 rounded-lg border transition ${
                filterDate
                  ? 'border-indigo-300 bg-indigo-50 text-indigo-600'
                  : 'border-gray-300 text-indigo-600 hover:bg-indigo-50'
              }`}
            >
              <Calendar className="w-5 h-5" />
            </button>
            {showDateFilter && (
              <div className="absolute left-0 top-full z-20 mt-2 rounded-lg bg-white p-3 shadow-lg border border-gray-200">
                <label htmlFor="attendance-date-filter" className="block text-xs font-medium text-gray-600 mb-1">
                  Filter by date
                </label>
                <input
                  id="attendance-date-filter"
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  autoFocus
                />
              </div>
            )}
            {filterDate && (
              <button
                type="button"
                onClick={() => { setFilterDate(''); setShowDateFilter(false); }}
                className="px-2 py-2 text-sm text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            )}
          </div>
          <button onClick={openCreateModal} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition">
            <Plus className="w-5 h-5" /> Mark Attendance
          </button>
        </div>
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{success}</span>
          <button onClick={() => setSuccess('')}><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">
            Attendance Sessions ({sessions.filter(session => !filterDate || session.session_date?.split('T')[0] === filterDate).length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Subject</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Grade</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sessions.filter(session => !filterDate || session.session_date?.split('T')[0] === filterDate).map((session) => (
                <tr key={session.session_id} className="hover:bg-indigo-50 cursor-pointer transition" onClick={() => navigate(`/lecturer/attendance/${session.session_id}`)}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-100 rounded-lg"><Calendar className="w-4 h-4 text-indigo-600" /></div>
                      <span className="font-medium text-gray-800">{new Date(session.session_date).toLocaleDateString()}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{session.subject_name}</td>
                  <td className="px-4 py-3"><span className="px-2 py-1 bg-blue-50 text-blue-600 rounded-full text-xs">{session.grade_name}</span></td>
                  <td className="px-4 py-3 text-gray-600">{session.session_description || '-'}</td>
                  <td className="px-4 py-3 flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => openDateEditModal(session)}
                      title={`Change attendance date from ${new Date(session.session_date).toLocaleDateString()}`}
                      className="flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition"
                    >
                      <Calendar className="w-4 h-4" />
                    </button>
                    <button onClick={() => openEditModal(session)} className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDeleteSession(session)} className="flex items-center gap-1 px-2 py-1 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {sessions.filter(session => !filterDate || session.session_date?.split('T')[0] === filterDate).length === 0 && (
                <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-400">No attendance sessions recorded</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mark Attendance Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 my-8 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">{editingSessionId ? 'Edit Attendance' : 'Mark Attendance'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            {error && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grade *</label>
                  <select required value={formData.grade_id} onChange={(e) => handleGradeChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500">
                    <option value="">Select Grade</option>
                    {grades.map(g => <option key={g.grade_id} value={g.grade_id}>{g.grade_name}</option>)}
                  </select>
                  {formData.grade_id && (
                    <p className="text-sm text-gray-500 mt-1">This will include <strong>all subjects</strong> and students in the selected grade.</p>
                  )}

                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Session Date *</label>
                  <input type="date" required value={formData.session_date} onChange={(e) => setFormData({...formData, session_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input type="text" value={formData.session_description} onChange={(e) => setFormData({...formData, session_description: e.target.value})}
                    placeholder="e.g., Poetry Analysis Lecture" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
                </div>
              </div>

              {/* Student List */}
              {students.length > 0 && (
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">Mark Attendance ({students.length} students)</label>
                    <div className="flex gap-2 text-xs">
                      <span className="flex items-center gap-1"><UserCheck className="w-3 h-3 text-green-500" /> Present</span>
                      <span className="flex items-center gap-1"><UserX className="w-3 h-3 text-red-500" /> Absent</span>
                      <span className="flex items-center gap-1 text-gray-500">⚪ Unmarked</span>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {students.map((student) => {
                      const att = attendanceData.find(a => a.student_id === student.user_id);
                      const status = att?.status || '';
                      const isPresent = status === 'Present';
                      const isAbsent = status === 'Absent';
                      const isUnmarked = status === '';
                      return (
                        <div key={student.user_id} className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition ${
                          isPresent ? 'bg-green-50 border border-green-200' : 
                          isAbsent ? 'bg-red-50 border border-red-200' : 
                          'bg-gray-50 border border-gray-200'
                        }`}
                          onClick={() => toggleAttendance(student.user_id)}>
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              isPresent ? 'bg-green-100 text-green-600' : 
                              isAbsent ? 'bg-red-100 text-red-600' : 
                              'bg-gray-200 text-gray-600'
                            }`}>
                              {student.full_name?.charAt(0)}
                            </div>
                            <span className="font-medium text-gray-800">{student.full_name}</span>
                          </div>
                          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            isPresent ? 'bg-green-100 text-green-600' : 
                            isAbsent ? 'bg-red-100 text-red-600' : 
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {isPresent && <Check className="w-3 h-3" />}
                            {isAbsent && <X className="w-3 h-3" />}
                            {isPresent ? 'Present' : isAbsent ? 'Absent' : 'Unmarked'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Present: {attendanceData.filter(a => a.status === 'Present').length} | 
                    Absent: {attendanceData.filter(a => a.status === 'Absent').length} | 
                    Unmarked: {attendanceData.filter(a => !a.status).length}
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={!editingSessionId && students.length === 0} className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                  {editingSessionId ? 'Update Attendance' : 'Save Attendance'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {dateEditSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDateEditSession(null)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Change Attendance Date</h2>
              <button type="button" onClick={() => setDateEditSession(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Update only the date for this attendance session. Student attendance statuses will not change.
            </p>
            <form onSubmit={saveSessionDate} className="space-y-4">
              <div>
                <label htmlFor="attendance-session-date" className="block text-sm font-medium text-gray-700 mb-1">Session date</label>
                <input
                  id="attendance-session-date"
                  type="date"
                  required
                  value={dateEditValue}
                  onChange={(event) => setDateEditValue(event.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setDateEditSession(null)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" disabled={savingDate} className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                  {savingDate ? 'Saving...' : 'Save Date'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
